
import React, { useEffect, useState } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate, Link } from 'react-router-dom';
import { Zap, RefreshCw, Trash2, ArrowLeft, User, StopCircle } from 'lucide-react';

interface VoteRecord {
    id: string;
    created_at: string;
    voter_name: string;
    driver_name: string;
}

export const VoteAdmin: React.FC = () => {
    const { user } = useAuth();
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [votes, setVotes] = useState<VoteRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);

    if (!user || (!user.roles.includes(UserRole.ADMIN) && !user.roles.includes(UserRole.WEBADMIN))) {
        return <Navigate to="/" />;
    }

    useEffect(() => {
        // Load completed events
        apiRequest<any[]>('events.php').then(data => {
            const completed = data.filter((e: any) => e.status === 'COMPLETED').reverse();
            setEvents(completed);
            if (completed.length > 0) setSelectedEventId(completed[0].id);
        });
    }, []);

    const loadVotes = async (eventId: string) => {
        if (!eventId) return;
        setLoading(true);
        setSelectedEventId(eventId);
        try {
            const res = await apiRequest<VoteRecord[]>(`vote.php?admin=true&eventId=${eventId}`);
            setVotes(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if(selectedEventId) loadVotes(selectedEventId);
    }, [selectedEventId]);

    const handleDelete = async (voteId: string) => {
        if(!confirm("Stimme wirklich löschen?")) return;
        try {
            await apiRequest(`vote.php?id=${voteId}`, 'DELETE');
            loadVotes(selectedEventId);
        } catch(e:any) {
            alert(e.message);
        }
    };

    const handleForceFinish = async () => {
        if(!selectedEventId) return;
        if(!confirm("Soll das Voting SOFORT beendet und ausgewertet werden?\n\n- Der Timer auf dem Dashboard verschwindet.\n- Der Gewinner wird ermittelt und Punkte/Badge vergeben.\n- Bei Gleichstand gewinnen alle.")) return;
        
        setProcessing(true);
        try {
            const res = await apiRequest<{message: string}>('vote.php', 'POST', {
                action: 'force_finish',
                eventId: selectedEventId
            });
            alert(res.message);
        } catch(e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <Link to="/" className="inline-flex items-center text-gray-500 hover:text-white mb-2 transition-colors font-bold uppercase text-xs tracking-widest"><ArrowLeft size={14} className="mr-2"/> Dashboard</Link>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                        <Zap size={32}/> DotD Voting Admin
                    </h1>
                    <p className="text-gray-400 text-sm">Überprüfe und bereinige Stimmen</p>
                </div>
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <select 
                        className="bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm flex-1 md:flex-none"
                        value={selectedEventId}
                        onChange={e => loadVotes(e.target.value)}
                    >
                        {events.map(e => (
                            <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>
                        ))}
                    </select>
                    
                    <Button variant="secondary" icon={RefreshCw} onClick={() => loadVotes(selectedEventId)} disabled={loading}></Button>
                    
                    <Button 
                        onClick={handleForceFinish} 
                        disabled={processing || !selectedEventId} 
                        className="bg-yellow-600 hover:bg-yellow-500 text-white border-none shadow-lg"
                    >
                        <StopCircle size={16} className="mr-2"/> {processing ? 'Beende...' : 'Voting beenden & Auswerten'}
                    </Button>
                </div>
            </div>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-900 text-gray-400 uppercase text-xs font-bold">
                            <tr>
                                <th className="p-3">Zeitpunkt</th>
                                <th className="p-3">Wähler (User)</th>
                                <th className="p-3">Gewählt (Fahrer)</th>
                                <th className="p-3 text-right">Aktion</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {votes.map(vote => (
                                <tr key={vote.id} className="hover:bg-gray-800/50 transition-colors">
                                    <td className="p-3 font-mono text-gray-500 text-xs">
                                        {new Date(vote.created_at).toLocaleString()}
                                    </td>
                                    <td className="p-3 font-bold text-gray-300 flex items-center gap-2">
                                        <User size={14}/> {vote.voter_name}
                                    </td>
                                    <td className="p-3 font-bold text-yellow-500">
                                        {vote.driver_name}
                                    </td>
                                    <td className="p-3 text-right">
                                        <button 
                                            onClick={() => handleDelete(vote.id)} 
                                            className="text-red-500 hover:text-red-400 bg-red-900/10 p-1.5 rounded border border-red-900/30"
                                            title="Löschen"
                                        >
                                            <Trash2 size={14}/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {votes.length === 0 && (
                        <div className="p-10 text-center text-gray-500 italic">Keine Stimmen für dieses Event gefunden.</div>
                    )}
                </div>
            </Card>
        </div>
    );
};
